import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../assets/js/application/sidenavaction';
import {BusinessEntityService} from '../../../../services/businessEntity.service';
import BusinessEntity from '../../../../services/businessEntity.model';
import FarmSiteModel from '../../../../services/farmSite.model';
import {FarmSiteService} from '../../../../services/farmSite.service';
import ReadingsModel from '../../../../services/readings.model';
import {ReadingsService} from '../../../../services/readings.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import BusinessTypeModel from 'src/app/services/businessType.model';
import {BusinessTypeService} from 'src/app/services/businessType.service';
import {ActivatedRoute} from '@angular/router';
import {ExcelService} from 'src/app/services/excelService';
import {PersonsService} from 'src/app/services/persons.service';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../Utils/batch-loader.util";


declare var $: any;

/**
 * @component WaterQualityHistoryComponent
 * @selector app-water-quality-history
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `WaterQualityHistoryComponent` is responsible for displaying and managing the historical records of water quality readings
 * for farm ponds. It provides a comprehensive view of various water parameters over time, allowing users to track trends,
 * identify anomalies, and ensure optimal conditions for aquaculture.
 *
 * ## Key Responsibilities:
 * 1. **Data Retrieval**: Fetches historical water quality readings based on selected business, farm site, and date range.
 * 2. **Filtering**: Offers robust filtering capabilities including:
 *    - Business and Farm Site selection.
 *    - Date range filtering.
 *    - Test type (e.g., Preparatory vs. Regular).
 *    - Text-based search across multiple fields (Serial Number, Pond Name, etc.).
 * 3. **Sorting**: Allows users to sort the history list by Date, Farm Name, Section Name, Pond Name, or Device Serial Number.
 * 4. **Pagination/Infinite Scroll**: Implements a batch loading mechanism to handle large datasets efficiently.
 * 5. **Export**: Facilitates the export of the filtered water quality history to an Excel file.
 * 6. **Role-Based Access**: Adapts data visibility and available options based on the user's role (Farmer, Farm Manager, Business Admin, Employee).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Access to all farm sites and readings for their business.
 *   - **Farm Manager**: Restricted access to farm sites assigned to them.
 * - **Provider Category Users**:
 *   - **Business Admin**: Access to readings for businesses they provide services to.
 *   - **Provider Manager**: Access to readings for businesses assigned to their technicians.
 * - **Employee**: Access to all farm sites (typically for internal admin/support).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context ('Farm', 'Provider').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for report headers.
 * - `LoginUserName`: Used for report headers.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial farm sites or businesses.
 * 2. **Selection**: User selects Business (if applicable) and Farm Site.
 * 3. **Data Fetching**: `getReatdings` calls `ReadingsService` to fetch historical data.
 * 4. **Display**: Data is loaded into `resultantReadings` via `BatchLoader`.
 * 5. **Filtering**: `onFiltersApplied` (implied via search/filter logic) filters the local dataset or triggers new fetches.
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad`, `responseLoader`, `listLoader` manage loading indicators.
 * - **Form**: `registerForm` handles selection criteria and validation.
 * - **Sorting**: `key`, `reverse`, and specific sort flags manage table sorting.
 */
@Component({
  selector: 'app-water-quality-history',
  templateUrl: './water-quality-history.component.html',
  styleUrls: ['./water-quality-history.component.scss']
})
export class WaterQualityHistoryComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} The currently selected business entity. */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Selected farm site model (unused in current logic). */
  public farmsiteEntity: FarmSiteModel;

  /** @type {Array<ReadingsModel>} List of readings fetched from the service. */
  public readings: Array<ReadingsModel>;

  /** @type {ReadingsModel} Model used for binding filter criteria. */
  public reading: ReadingsModel;

  /** @type {Array<BusinessTypeModel>} List of business types (unused in current logic). */
  public businessTypes: Array<BusinessTypeModel>;

  /** @type {BusinessTypeModel} Single business type model (unused in current logic). */
  public btype: BusinessTypeModel;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {boolean} Flag for page loading state. */
  public pgLoad = false;

  // sorting pagination
  /** @type {string} Current sort key. Default 'name'. */
  public key: string = 'name';

  /** @type {boolean} Current sort direction (reverse). Default false. */
  public reverse: boolean = false;

  /** @type {any} Object storing display conditions for reading parameters. */
  public readingsCondtions: any;

  /** @type {any} Array of reading parameter constants. */
  public readingsConstants: any;

  /** @type {Array<BusinessEntity>} Duplicate list of business entities (unused). */
  public businessDetails: Array<BusinessEntity>;

  /** @type {boolean} Flag indicating if search button has been clicked. */
  public isSearchClicked: boolean;

  /** @type {Array<any>} Array of farm site IDs for filtering. */
  public farmSiteIds = new Array();

  /** @type {boolean} Flag controlling the response loader. Default true. */
  public responseLoader: boolean = true;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {boolean} Flag for sorting by Date. */
  public dateAsc: boolean = false;

  /** @type {boolean} Flag for sorting by Section Name. */
  public sectionnameAsc: boolean = false;

  /** @type {boolean} Flag for sorting by Pond Name. */
  public pondnameAsc: boolean = false;

  /** @type {boolean} Flag for sorting by Serial Number. */
  public serialnumberAsc: boolean = false;

  /** @type {boolean} Flag for sorting by Farm Name. */
  public farmnameAsc: boolean = false;

  /** @type {string} Host name (unused). */
  public hostName: string;

  /** @type {ReadingsModel} Input data model for fetching readings. */
  public inputData = new ReadingsModel();

  /** @type {Array<any>} The list of processed readings currently displayed. */
  public resultantReadings = new Array();

  /** @type {Array<any>} Original list of readings before filtering. */
  public originalHistoryList = new Array();

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {string} Generic item holder (unused). */
  public item: string;

  /** @type {any} Array of business type IDs for filtering provider businesses. */
  public inputBusinessType: any;

  /** @type {boolean} Flag for dropdown visibility. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered options for business dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Business search input placeholder/value. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag to disable submit button during processing. */
  public submitButtonDisabled: boolean;

  /**
   * @method sort
   * @description Sets the sort key and toggles the sort direction.
   * @param {string} key - The property name to sort by.
   */
  public sort(key: string) {
    this.key = key;
    this.reverse = !this.reverse;
  }

  // pagination sorting
  /** @type {number} Current page number. Default 1. */
  public currentpagenum: number = 1;

  /** @type {number} Items per page. Default 15. */
  public noofitemsPerPage: number = 15;

  /* End pagination, sorting, filter*/
  /** @type {FormGroup} Main reactive form for filter criteria. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag indicating if form submitted (unused). */
  public submitted = false;

  /** @type {boolean} Flag controlling list loader spinner. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if filtering is active. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if list is empty. */
  public noList: boolean = false;

  /** @type {boolean} Flag to enable/disable page loader (unused). */
  public enableDisablePageLoader: boolean = false;

  /** @type {any} Timeout for search debounce (unused). */
  searchTimeout: any;

  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;

  /** @type {Date} Maximum allowed date for date picker. */
  maxDate = new Date();

  /** @type {FormGroup} Nested form group for date range. */
  dateRangeField: FormGroup

  /** @type {{startDate: moment.Moment, endDate: moment.Moment}} Selected date range. */
  selected: {startDate: moment.Moment, endDate: moment.Moment};

  /** @type {boolean} Flag indicating if a date range is selected. */
  isDateRangeSelected: boolean = false;

  /**
   * Constructor for WaterQualityHistoryComponent.
   * Initializes services and the reactive form.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ExcelService} excelService - Service for Excel export.
   * @param {BusinessTypeService} businessTypeService - Service for business type operations.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site operations.
   * @param {PersonsService} personService - Service for person/technician operations.
   * @param {ReadingsService} readingsService - Service for readings operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service ToastrService
   * @service FormBuilder
   * @service ExcelService
   * @service BusinessTypeService
   * @service ActivatedRoute
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service PersonsService
   * @service ReadingsService
   * @service Sidenavaction
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public formBuilder: FormBuilder, public excelService: ExcelService, public businessTypeService: BusinessTypeService, public route: ActivatedRoute, public businessEntityService: BusinessEntityService, public farmsiteEntityService: FarmSiteService, public personService: PersonsService, public readingsService: ReadingsService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.reading = new ReadingsModel();
    // Initialize the register form with validation
    this.registerForm = this.formBuilder.group({
      businessnameField: [null],
      dateRangeField: this.formBuilder.group({
        start: [null],
        end: [null]
      }),
      prepdaratory: [null],
      testtype: [null],
      locationameField: [null],
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes date picker and default values.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Provider**: Fetches provider-specific data.
   *   - **Default**: Fetches all business entities for selection.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.isFiltering = false;
    this.resultantReadings = [];
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
    this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.keyword = 'businessName';
    this.inputBusinessType = new Array();
    try {
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      /* user login details */
      if (localStorage.length > 0) {
        this.LoginUserName = localStorage.getItem('LoginUserName');
      }
      /* user login details end */
      this.businessEntities = new Array();
      this.reading.farm_site_id = '';
      this.reading.isPreparatory = '';
      this.reading.testType = '';
      this.readingsConstants = ['time', 'serial_nember', 'name', 'section_name', 'time_interval', 'pH', 'turbidity', 'salinity', 'do2', 'conductivity', 'tds', 'temperature', 'nitrite', 'calcium', 'magnesium', 'hardness', 'alkalinity', 'carbonate', 'bicarbonate', 'tan', 'uam', 'ta', 'sg', 'comment'];
      this.readingsCondtions = {
        serial_nember: false,
        name: false,
        section_name: false,
        time_interval: false,
        time: false,
        turbidity: false,
        salinity: false,
        do2: false,
        conductivity: false,
        tds: false,
        temperature: false,
        nitrite: false,
        calcium: false,
        magnesium: false,
        hardness: false,
        alkalinity: false,
        carbonate: false,
        bicarbonate: false,
        tan: false,
        uam: false,
        ta: false,
        sg: false,
        comment: false
      };
      this.isSearchClicked = false;
      this.businessEntities = await this.businessEntityService.getAllBusiness();
      this.businessDetails = this.businessEntities;
      this.pgLoad = true;
      this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
      this.btype = this.businessTypes.find(businessType => businessType && businessType.id === '618a17b80f339c001e991b03');
      this.inputBusinessType.push(this.btype.id);
      let btype = this.businessTypes.find(businessType => businessType && businessType.id === '632aeccb0d7d43001d8813c2');
      this.inputBusinessType.push(btype.id);
      this.businessSearch = undefined;
      this.registerForm.get('businessnameField').patchValue('');
      this.businessEntities = await this.businessEntityService.getProviderBusiness(this.inputBusinessType);
      this.filteredOptions = this.businessEntities;
      this.businessEntity = JSON.parse(localStorage.getItem('businessEntityById'));
      if (this.businessEntity !== undefined) {
        if ((this.loginPersonType == 'Business Admin' || this.loginPersonType == 'Provider Manager') && this.loginUserBusinessCategory == 'Provider') {
          this.registerForm.get('businessnameField').patchValue('this.loginUserBusinessName');
          this.reading.farmProviderId = this.businessEntity.id;
          this.getAllFarmsitesByFeedProviderID();
          this.reading.businessName = this.loginUserBusinessName;
        }
      }
      this.reading.farm_site_id = '';
      this.reading.farmProviderId = '';
      this.farmSiteIds = new Array();
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.registerForm.get('businessnameField').patchValue('this.loginUserBusinessName');
        this.farmSites = await this.farmsiteEntityService.getFarmeSitesByFarmId(localStorage.businessID);
        this.reading.businessName = this.loginUserBusinessName;
        this.pgLoad = true;
        this.farmSiteIds = new Array();
        for (let i = 0; i < this.farmSites.length; i++) {
          this.farmSiteIds.push(this.farmSites[i].id);
        }
        this.setSingleValueForFormLocation();
      }
      if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmsiteEntityService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        this.pgLoad = true;
        this.reading.businessName = this.loginUserBusinessName;
        this.farmSiteIds = new Array();
        for (let i = 0; i < this.farmSites.length; i++) {
          this.farmSiteIds.push(this.farmSites[i].id);
        }
        this.setSingleValueForFormLocation();
      }
      this.responseLoader = true;
      if (this.loginPersonType == 'EMPLOYEE') {
        this.farmSites = await this.farmsiteEntityService.getAllFarmSites();
        this.reading.businessName = this.loginUserBusinessName;
        this.pgLoad = true;
        for (let i = 0; i < this.farmSites.length; i++) {
          this.farmSiteIds.push(this.farmSites[i].id);
        }
        this.setSingleValueForFormLocation();
      }
      this.initializedatePicker();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

   /**
    * @method initializedatePicker
    * @description Initializes the date picker form group and subscribes to value changes.
    * @side-effect Updates `isDateRangeSelected`.
    */
   public initializedatePicker(): void {
    // grab the nested form-group
    const dateRangeGroup = this.registerForm.get('dateRangeField') as FormGroup;

    // react whenever user changes either date
    dateRangeGroup.valueChanges.subscribe(({ start, end }) => {
      if (!start || !end) {
        this.isDateRangeSelected = false;
      }
    });
  }

  /**
   * @method onPickerClosed
   * @description Handles the closing of the date picker. Ensures valid range selection.
   * @side-effect Updates `dateRangeField`, `isDateRangeSelected`, calls `onDateChanged`.
   */
  public onPickerClosed(): void {
    const dateRangeGroup = this.registerForm.get('dateRangeField') as FormGroup;
    const { start, end } = dateRangeGroup.value;

    if (start && !end) {
      // only start chosen → copy it to end
      dateRangeGroup.patchValue({ end: start }, { emitEvent: false });
      this.isDateRangeSelected = true;
      this.onDateChanged({ startDate: start, endDate: start });

    } else if (start && end) {
      this.isDateRangeSelected = true;
      this.onDateChanged({ startDate: start, endDate: end });

    } else {
      this.isDateRangeSelected = false;
    }
  }

  /**
   * @method onDateChanged
   * @description Updates the `selected` date range object when dates change.
   * @param {{ startDate: Date; endDate: Date }} range - The new date range.
   * @side-effect Updates `selected`, `isDateRangeSelected`.
   */
  public onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate:   moment(range.endDate)
    };
    this.isDateRangeSelected = true;
  }

  /**
   * @method clearDateRange
   * @description Resets the date range filter.
   * @side-effect Resets `dateRangeField`, `isDateRangeSelected`, `selected`.
   */
  public clearDateRange(): void {
    (this.registerForm.get('dateRangeField') as FormGroup).reset();
    this.isDateRangeSelected = false;
    this.selected = undefined;
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

   /**
    * @method filterBusinesses
    * @description Filters business entities based on search input.
    * Updates `filteredOptions`.
    * @side-effect Updates `filteredOptions`.
    */
   public filterBusinesses(): void {
      const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
      const searchText =
        typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
      this.filteredOptions = this.businessEntities.filter(o =>
        o.businessName.toLowerCase().includes(searchText)
      );
    }

    /**
     * @method selectOption
     * @description Handles business selection from dropdown.
     * Updates form value and triggers fetching of farm sites for provider.
     * @param {BusinessEntity} business - The selected business entity.
     * @side-effect Updates `registerForm`, `isDropdownOpen`, `businessSearch`, `reading.farmProviderId`, calls `getAllFarmsitesByFeedProviderID`.
     */
    public selectOption(business: BusinessEntity): void {
      this.registerForm.get('businessnameField')!.setValue(business.businessName);
      this.isDropdownOpen = false;
      this.businessSearch = business.businessName;
      this.reading.farmProviderId = business.id;
      if (this.reading.farmProviderId && (this.reading.farmProviderId != undefined || this.reading.farmProviderId != null)) {
        this.getAllFarmsitesByFeedProviderID();
      }
    }

  /* End get all farmsite by login persontype is admin */
  // get all providers end

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.farmsiteEntityService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllFarmsitesByFeedProviderID
   * @async
   * @description Fetches farm sites based on the selected feed provider ID.
   * Handles logic for Business Admin (Provider) and Provider Manager roles.
   * @side-effect Updates `farmSites`, `farmSiteIds`, `pgLoad`, calls `setSingleValueForFormLocation`.
   */
  public async getAllFarmsitesByFeedProviderID() {
    try {
      this.isSearchClicked = false;
      if (this.reading.farmProviderId) {
        if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
          this.farmSites = await this.farmsiteEntityService.getFarmeSitesBusinessDetailsByProviderId(this.reading.farmProviderId);
        } else {
          let technicians = await this.personService.getAllTechniciansByProviderManagerId(localStorage.personID);
          let technicianIds = technicians.length > 0 ? technicians.map(technician => technician.id) : [];
          this.farmSites = await this.farmsiteEntityService.getFarmeSitesByTechnicianIDs(technicianIds);
        }
        this.pgLoad = true;
        this.farmSiteIds = new Array();
        if (this.farmSites) {
          for (let i = 0; i < this.farmSites.length; i++) {
            this.farmSiteIds.push(this.farmSites[i].id);
          }
        }
      } else {
        this.farmSiteIds = new Array();
        if (this.farmSites) {
          for (let i = 0; i < this.farmSites.length; i++) {
            this.farmSiteIds.push(this.farmSites[i].id);
          }
        }
        this.setSingleValueForFormLocation();
      }
      this.setSingleValueForFormLocation();
      /*find business name by selected businessid*/
      const selectedBusinessObj = _.filter(this.businessEntities, (o) => {
        return o.id == this.reading.business_id;
      });
      if (selectedBusinessObj.length > 0) {
        this.reading.businessName = selectedBusinessObj[0].businessName;
      }
      /*find business name by selected businessid*/
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-selects the farm site if only one exists.
   * @side-effect Updates `reading.farm_site_id`.
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.reading.farm_site_id = this.farmSites[0].id; // model-binding key
    }
  }

  /* End get farmsites by login FeedProviderID */

  /**
   * @method handleReadingsTable
   * @description Resets the search clicked flag.
   * @side-effect Updates `isSearchClicked`.
   */
  public handleReadingsTable() {
    this.isSearchClicked = false;
  }

  /**
   * @method getReatdings
   * @async
   * @description Fetches water quality readings based on selected criteria.
   * Validates form, constructs input data, calls service, processes response, and initializes batch loader.
   * @side-effect Updates `readings`, `resultantReadings`, `originalHistoryList`, `batchLoader`.
   */
  public async getReatdings() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    this.readings = new Array();
    this.submitButtonDisabled = true;
    try {
      if (!this.reading.farm_site_id && (this.loginPersonType != 'Business Admin' && this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager')) {
        this.toasterService.clear();
        this.toasterService.error('Please select farmsite', '', {
          timeOut: 2000
        });
        return;
      }
      this.inputData = new ReadingsModel()
      this.responseLoader = false;
      if ((this.selected && this.selected.startDate) && (this.selected && this.selected.endDate) && this.isDateRangeSelected) {
        this.inputData['toDate'] = moment(this.selected.endDate).format('YYYY-MM-DD');
        this.inputData['fromDate'] = moment(this.selected.startDate).format('YYYY-MM-DD');
      }
      if (this.reading.farm_site_id) { // if user selected farmsite id then it will goes to backened for all users
        this.inputData['farmSite'] = [this.reading.farm_site_id];
      } else { // if user selected reading.farmSiteIdsArr then it will goes to backened for all users
        this.inputData['farmSite'] = this.farmSiteIds;
      }
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && !this.reading.farm_site_id) {
        this.inputData['farmSite'] = this.farmSiteIds;
      }
      this.inputData['isPreparatory'] = this.reading.isPreparatory ? Number(this.reading.isPreparatory) : 0;
      this.inputData['testType'] = this.reading.testType ? Number(this.reading.testType) : 0;
      if (this.reading.testType == null || this.reading.testType == undefined) {
        this.inputData['testType'] = 0;
      }
      /*find selected farmsite name*/
      const selectedFarmsiteObj = _.filter(this.farmSites, (o) => {
        return o.id == this.reading.farm_site_id;
      });
      if (selectedFarmsiteObj.length > 0) {
        this.inputData['farmNameAtLocation'] = selectedFarmsiteObj[0] && selectedFarmsiteObj[0].farmNameAtLocation;
        if (this.loginPersonType == 'EMPLOYEE') {
          let business = this.businessDetails.filter(item => item && item.id === (selectedFarmsiteObj[0] && selectedFarmsiteObj[0].businessID));
          this.inputData['businessName'] = business[0] && business[0].businessName;
        }
      }
      /*end find selected farmsite name*/
      this.readings = await this.readingsService.getReadingsByDate(this.inputData);
      this.submitButtonDisabled = false;
      this.responseLoader = true;
      if (this.readings) {
        for (let i = 0; i < this.readings.length; i++) {
          let dateb = this.readings[i].time;
          if (dateb) {
            this.readings[i].time = moment(dateb).utc().format('DD-MMM-YYYY h:mm:ss a');
          }
        }
      }
      this.resultantReadings = new Array();
      if (this.readings && this.readings.length > 0) {
        this.isSearchClicked = true;
        this.resultantReadings = [];
        for (let i = 0; i < this.readings.length; i++) {
          let eachObj = this.readings[i];
          if (eachObj.editedReadings) {
            let editedReading: any;
            editedReading = this.readings[i].editedReadings;
            editedReading.pond = this.readings[i].pond;
            editedReading.pondCulture = this.readings[i].pondCulture;
            editedReading.device = this.readings[i].device;
            editedReading.farm_name = this.readings[i].farm_name;
            this.resultantReadings.push(editedReading);
          } else {
            this.resultantReadings.push(this.readings[i]);
          }
        }
        for (let i = 0; i < this.resultantReadings.length; i++) {
          let eachObj = this.resultantReadings[i];
          for (let rc = 0; rc < this.readingsConstants.length; rc++) {
            if ((!eachObj[this.readingsConstants[rc]] || eachObj[this.readingsConstants[rc]] == -1) && eachObj[this.readingsConstants[rc]] != 0) {
              this.resultantReadings[i][this.readingsConstants[rc]] = '--NA--';
            } else {
              this.readingsCondtions[this.readingsConstants[rc]] = true;
            }
            if (this.readingsConstants[rc] == 'nitrite' && eachObj[this.readingsConstants[rc]] == 0 && !eachObj[this.readingsConstants[rc]] == true) {
              this.resultantReadings[i].nitrite = (this.resultantReadings[i].nitrite == -1 || this.resultantReadings[i].nitrite == null) ? 'NA' : this.resultantReadings[i].nitrite;
              this.readingsCondtions[this.readingsConstants[rc]] = true;
            }
          }
        }
        for (let i = 0; i < this.resultantReadings.length; i++) {
          if (this.resultantReadings[i].cust_added_params != undefined && this.resultantReadings[i].cust_added_params != '' && this.resultantReadings[i].cust_added_params != null) {
            let adpVal = this.resultantReadings[i].cust_added_params;
            adpVal = JSON.parse(adpVal);
            this.resultantReadings[i].cust_added_params = adpVal;
            this.resultantReadings[i].T_Ammonia = 'NA';
            this.resultantReadings[i].Bio_Floc = 'NA';
            this.resultantReadings[i].Calcium = 'NA';
            this.resultantReadings[i].Magnesium = 'NA';
            this.resultantReadings[i].Hardness = 'NA';
            this.resultantReadings[i].Carbonate = 'NA';
            this.resultantReadings[i].BiCarbonate = 'NA';
            for (let a = 0; a < this.resultantReadings[i].cust_added_params.length; a++) {
              let key = this.resultantReadings[i].cust_added_params[a].key;
              if ((this.resultantReadings[i].cust_added_params[a].key == 'Ammonia (ppm)')) {
                key = key.split(' ')[0].toString();
              }
              if ((this.resultantReadings[i].cust_added_params[a].key == 'Bio-Floc (ml/L)')) {
                key = key.split(' ')[0].toString();
              }
              if (this.resultantReadings[i].cust_added_params[a].key == 'Calcium (ppm)') {
                key = key.split(' ')[0].toString();
              }
              if (this.resultantReadings[i].cust_added_params[a].key == 'Magnesium (ppm)') {
                key = key.split(' ')[0].toString();
              }
              if (this.resultantReadings[i].cust_added_params[a].key == 'Carbonates (ppm)') {
                key = key.split(' ')[0].toString();
              }
              if (this.resultantReadings[i].cust_added_params[a].key == 'Bicarbonates (ppm)') {
                key = key.split(' ')[0].toString();
              }
              if (this.resultantReadings[i].cust_added_params[a].key == 'Hardness (ppm)') {
                key = key.split(' ')[0].toString();
              }
              let value = this.resultantReadings[i].cust_added_params[a].value;
              if ('Bio-Floc' == key) {
                this.resultantReadings[i].Bio_Floc = Number(value);
              }
              if ('Ammonia' == key) {
                this.resultantReadings[i].T_Ammonia = Number(value);
              }
              if ('Calcium' == key) {
                this.resultantReadings[i].Calcium = Number(value);
              }
              if ('Magnesium' == key) {
                this.resultantReadings[i].Magnesium = Number(value);
              }
              if ('Carbonates' == key) {
                this.resultantReadings[i].Carbonate = Number(value);
              }
              if ('Bicarbonates' == key) {
                this.resultantReadings[i].BiCarbonate = Number(value);
              }
              if ('Hardness' == key) {
                this.resultantReadings[i].Hardness = Number(value);
              }
            }
          } else {
            this.resultantReadings[i].T_Ammonia = 'NA';
            this.resultantReadings[i].Bio_Floc = 'NA';
            this.resultantReadings[i].Calcium = 'NA';
            this.resultantReadings[i].BiCarbonate = 'NA';
            this.resultantReadings[i].Carbonate = 'NA';
            this.resultantReadings[i].Magnesium = 'NA';
            this.resultantReadings[i].Hardness = 'NA';
          }
        }
        this.originalHistoryList = this.resultantReadings;
        this.initializeBatchLoader(); // Reset instance
        this.batchLoader?.reset();     // Clear state
        await this.batchLoader?.loadNextBatch(true); // Load first batch
      } else {
        this.isSearchClicked = false;
        this.toasterService.warning('Data not available');
        this.submitButtonDisabled = false;
      }
      this.item = undefined;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.resultantReadings : this.originalHistoryList,
      (updatedList) => {
        this.resultantReadings = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method exportExcel
   * @async
   * @description Generates and downloads an Excel report of the water quality readings.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async exportExcel() {
    try {
      const fileObj = this.readingsService.excelReportsDownload('reading', this.inputData);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'reading');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method refereshFunction
   * @async
   * @description Refreshes the readings data by re-fetching from the server.
   * Formats the time in the response.
   */
  public async refereshFunction() {
    try {
      this.readings = await this.readingsService.getReadingsByDate(this.inputData);
      if (this.readings) {
        for (let i = 0; i < this.readings.length; i++) {
          let dateb = this.readings[i].time;
          if (dateb) {
            this.readings[i].time = moment(dateb).utc().format('DD-MMM-YYYY h:mm:ss a');
          }
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method aqucmdate
   * @description Sets default date range (today) if test type is 1 (Preparatory?).
   * @side-effect Updates `reading.from_date`, `reading.to_date`.
   */
  public aqucmdate() {
    if (this.reading.testType == 1) {
      this.reading.from_date = moment(new Date()).format('DD-MMM-YYYY'); // 2020-05-20
      this.reading.to_date = moment(new Date()).format('DD-MMM-YYYY'); // 2020-05-20
    } else {
      this.reading.from_date = undefined;
      this.reading.to_date = undefined;
    }
  }

  /**
   * @method sortingHistory
   * @description Sorts the displayed readings list based on the specified type.
   * Supports sorting by 'date', 'farmname', 'sectionname', 'pondname', 'serialnumber'.
   * @param {string} type - The field to sort by.
   */
  public sortingHistory(type: string) {
    try {
      if (type == 'date') {
        this.dateAsc = !this.dateAsc;
        let orderbyval = 'desc';
        if (this.dateAsc == true) {
          orderbyval = 'asc';
        }
        this.resultantReadings = _.orderBy(this.readings, ['time'], [orderbyval]);
        this.sectionnameAsc = false;
        this.pondnameAsc = false;
        this.serialnumberAsc = false;
        this.farmnameAsc = false;
      }
      if (type == 'farmname') {
        this.farmnameAsc = !this.farmnameAsc;
        let orderbyval = 'desc';
        if (this.farmnameAsc == true) {
          orderbyval = 'asc';
        }
        this.resultantReadings = _.orderBy(this.readings, ['pond.PondSection.section_name'], [orderbyval]);
        this.pondnameAsc = false;
        this.serialnumberAsc = false;
        this.dateAsc = false;
        this.sectionnameAsc = false;
      }
      if (type == 'sectionname') {
        this.sectionnameAsc = !this.sectionnameAsc;
        let orderbyval = 'desc';
        if (this.sectionnameAsc == true) {
          orderbyval = 'asc';
        }
        this.resultantReadings = _.orderBy(this.readings, ['pond.PondSection.section_name'], [orderbyval]);
        this.pondnameAsc = false;
        this.serialnumberAsc = false;
        this.dateAsc = false;
        this.farmnameAsc = false;
      }
      if (type == 'pondname') {
        this.pondnameAsc = !this.pondnameAsc;
        let orderbyval = 'desc';
        if (this.pondnameAsc == true) {
          orderbyval = 'asc';
        }
        this.resultantReadings = _.orderBy(this.readings, ['pond.name'], [orderbyval]);
        this.serialnumberAsc = false;
        this.dateAsc = false;
        this.sectionnameAsc = false;
        this.farmnameAsc = false;
      }
      if (type == 'serialnumber') {
        this.serialnumberAsc = !this.serialnumberAsc;
        let orderbyval = 'desc';
        if (this.serialnumberAsc == true) {
          orderbyval = 'asc';
        }
        this.resultantReadings = _.orderBy(this.readings, ['device.serial_number'], [orderbyval]);
        this.pondnameAsc = false;
        this.dateAsc = false;
        this.sectionnameAsc = false;
        this.farmnameAsc = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }
}
